import {
  AcademicYearAvailabilityPayload,
  AcademicYearDTO,
  BackdateType,
  CourseAvailabilityPayload,
  CourseAvailabilityType,
  DateAvailabilityPayload,
  MonthAvailabilityPayload
} from '@beable/roster-backend-client-v1';
import { Box, Checkbox, Grid, MenuItem, Select, Typography } from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { useTheme } from '@mui/material/styles';
import moment from 'moment';
import React, { ChangeEvent, useEffect, useState } from 'react';
import ConfirmDialog from '../../Common/ConfirmDialog';
import { getStyles } from '../styles';
import { DatePicker } from '../../Common/DatePicker/DatePicker';

interface AvailabilityProps {
  availabilityType?: CourseAvailabilityType;
  availabilityPayload?: CourseAvailabilityPayload;
  handleTypeChange(type: CourseAvailabilityType): void;
  handlePayloadChange(payload: CourseAvailabilityPayload): void;
  currentAcademicYears?: AcademicYearDTO[];
  oldStart?: Date;
}

export default function Availability({ availabilityType, availabilityPayload, handleTypeChange, handlePayloadChange, currentAcademicYears, oldStart }: AvailabilityProps) {  
  const [availability, setAvailability] = useState('' as CourseAvailabilityType);
  const [firstAYStart, setFirstAYStart] = useState(new Date());

  useEffect(() => {
    if (availabilityType) {
      setAvailability(availabilityType)
    }
  }, [availabilityType])

  useEffect(() => {
    if (availability) {
      handleTypeChange(availability)
      handlePayloadChange({} as CourseAvailabilityPayload)
    }
  }, [availability, handleTypeChange, handlePayloadChange])

  useEffect(() => {
    if(currentAcademicYears && currentAcademicYears.length) {
      const firstAY = currentAcademicYears.reduce((c, n) =>
        n.startAt < c.startAt ? n : c
      );
      setFirstAYStart(firstAY.startAt);
    }
  }, [currentAcademicYears]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selection = event.target.value as CourseAvailabilityType;
    const typeKey = Object.keys(CourseAvailabilityType).find(type =>
      CourseAvailabilityType[type as keyof typeof CourseAvailabilityType] === selection
    );

    if (typeKey) {
      setAvailability(CourseAvailabilityType[typeKey as keyof typeof CourseAvailabilityType]);
    }
  };

  return (
    <Grid container size={12}>
      <Grid size={2}>
        <Typography>
          Availability
        </Typography>
      </Grid>
      <Grid size={10}>
      <RadioGroup aria-label="availability" value={availability} onChange={handleChange}>
        <FormControlLabel
          value={CourseAvailabilityType.Custom}
          control={<Radio />}
          label={<Typography variant='body2'>Custom Dates</Typography>}
        />
        { availability === CourseAvailabilityType.Custom &&
          <CustomDates
            payload={availabilityPayload}
            handlePayloadChange={handlePayloadChange}
            firstAYStart={new Date(firstAYStart)}
          />
        }
        <FormControlLabel
          value={CourseAvailabilityType.AcademicYear}
          control={<Radio />}
          label={<Typography variant='body2'>Academic Year Start Date to Academic Year End Date</Typography>}
        />
        { availability === CourseAvailabilityType.AcademicYear &&
            <AcademicYear
              payload={availabilityPayload}
              handlePayloadChange={handlePayloadChange}
              firstAYStart={new Date(firstAYStart)}
              oldStart={oldStart && new Date(oldStart)}
            />
        }
        <FormControlLabel
          value={CourseAvailabilityType.Month}
          control={<Radio />}
          label={<Typography variant='body2'>Make Course Available Specific Month</Typography>}
        />
        { availability === CourseAvailabilityType.Month &&
          <Month
            payload={availabilityPayload}
            handlePayloadChange={handlePayloadChange}
            firstAYStart={firstAYStart}
          />
        }
      </RadioGroup>
      </Grid>
    </Grid>
  );
}

interface AvailabilityPayloadProps {
  payload?: CourseAvailabilityPayload;
  handlePayloadChange(payload: CourseAvailabilityPayload): void;
  firstAYStart: Date;
  oldStart?: Date;
}

function Month({ payload, handlePayloadChange }: AvailabilityPayloadProps) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const [month, setMonth] = useState(0);

  useEffect(() => {
    const monthPayload = payload as MonthAvailabilityPayload;
    if (monthPayload && monthPayload.month) {
      setMonth(monthPayload.month)
    }
  }, [payload])

  useEffect(() => {
    if (month && month > 0) {
      const payload: MonthAvailabilityPayload = { month };
      handlePayloadChange(payload);
    }
  }, [month, handlePayloadChange])

  const handleChange = (event: { target: { value: any } }) => {
    setMonth(parseInt((event.target as HTMLInputElement).value));
  };

  function getMonths() {
    return [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];
  }

  return (
    <Box sx={styles.availabilityOption}>
      <Select
          value={month}
          onChange={handleChange}
          sx={styles.availabilityInput}
      >
        {getMonths().map((month, index) => (
          <MenuItem key={index} value={index + 1}>{month}</MenuItem>
        ))}
      </Select>
    </Box>
  );
}

function AcademicYear({ payload, handlePayloadChange, firstAYStart, oldStart }: AvailabilityPayloadProps) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const [schoolStart, setSchoolStart] = useState<boolean>(false);
  const [origStart, setOrigStart] = useState<boolean>(false);
  const [noBackdate, setNoBackdate] = useState<boolean>(false);

  useEffect(() => {
    const datePayload = payload as AcademicYearAvailabilityPayload;
    if (datePayload) {
      setSchoolStart(datePayload.backdateType === BackdateType.SchoolStart)
      setOrigStart(datePayload.backdateType === BackdateType.OldStart)
      setNoBackdate(datePayload.backdateType === BackdateType.None)
    }
  }, [payload])

  useEffect(() => {
    if (schoolStart) {
      setOrigStart(false);
      setNoBackdate(false);
    }
  }, [schoolStart]);

  useEffect(() => {
    if (origStart) {
      setSchoolStart(false)
      setNoBackdate(false);
    }
  }, [origStart]);

  useEffect(() => {
    if (noBackdate) {
      setSchoolStart(false)
      setOrigStart(false)
    }
  }, [noBackdate]);

  useEffect(() => {
    let backdateType: BackdateType;
    if(origStart) {
      backdateType = BackdateType.OldStart;
    } else if (schoolStart) {
      backdateType = BackdateType.SchoolStart;
    } else {
      backdateType = BackdateType.None
    }
    handlePayloadChange({ backdateType: backdateType });
  }, [origStart, schoolStart, noBackdate, handlePayloadChange]);

  return (
    <Box sx={styles.availabilityOption}>
    { (firstAYStart < new Date()) && <FormControlLabel
          control={
            <Checkbox
              checked={noBackdate || false}
              name='useToday'
              color='secondary'
              onChange={() => setNoBackdate(!noBackdate) }
            />
          }
          label={<Typography variant='body2'>Start course from today</Typography>}
      /> }
      { (firstAYStart < new Date()) && <FormControlLabel
          control={
            <Checkbox
              checked={schoolStart || false}
              name='useSchoolStart'
              color='secondary'
              onChange={() => setSchoolStart(!schoolStart)}
            />
          }
          label={<Typography variant='body2'>Start course from school year start (in past)</Typography>}
        />}
      { (oldStart && moment(firstAYStart).add(2, 'days').isBefore(moment(oldStart)) && oldStart < new Date()) && <FormControlLabel
          control={
            <Checkbox
              checked={origStart || false}
              name='allowBackdate'
              color='secondary'
              onChange={() => setOrigStart(!origStart)}
            />
          }
          label={<Typography variant='body2'>{ `Use previously set start date for current session (${ moment(oldStart).format('MM/DD/YYYY') })` }</Typography>}
        /> }
    </Box>
    );
}

function CustomDates({ payload, handlePayloadChange, firstAYStart }: AvailabilityPayloadProps) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(moment().add(1, 'day').toDate());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dateString, setDateString] = useState<'schoolYear' | 'date' | undefined>(undefined);
  const [backdate, setBackdate] = useState<boolean | null>(null);
  const [outsideAY, setOutsideAY] = useState<boolean | null>(null);

  useEffect(() => {
    const datePayload = payload as DateAvailabilityPayload;
    if (datePayload && datePayload.startDate) {
      const backdateSet = datePayload.backdateType && datePayload.backdateType !== BackdateType.None;
      setStartDate(moment(datePayload.startDate).toDate())
      setEndDate(moment(datePayload.endDate).toDate())
      setBackdate(moment(datePayload.startDate).isBefore(moment()) || backdateSet)
      setOutsideAY(datePayload.outsideAcademicYear || false);

      const today = new Date();
      today.setHours(0,0,0,0);

      if(new Date(datePayload.startDate + ' ' + today.toTimeString()) < today && !backdateSet) {
        setDialogOpen(true)
      }
    }
  }, [payload])

  useEffect(() => {
    if ( startDate && endDate
      && startDate < endDate
    ) {
      const payload: DateAvailabilityPayload = {
        startDate: moment(startDate).format('YYYY-MM-DD'),
        endDate: moment(endDate).format('YYYY-MM-DD'),
        backdateType: backdate ? BackdateType.UseDate : BackdateType.None || BackdateType.None,
        outsideAcademicYear: outsideAY ? outsideAY : false
      };
      handlePayloadChange(payload);
    } else {
      handlePayloadChange({} as DateAvailabilityPayload);
    }
  }, [startDate, endDate, backdate, outsideAY, handlePayloadChange])

  const getMinEndDate = () => {
    if (startDate) {
      return moment(startDate).add(1, 'd').toDate();
    }
  }

  const onConfirmToday = () => {
    setDialogOpen(false);
    setBackdate(false);
    setStartDate(new Date());
  }

  const onConfirmDate = () => {
    setDialogOpen(false);
    setBackdate(true);
  }

  const backdateChanged = (backdate: boolean) => {
    setBackdate(backdate);
    if(!backdate) {
      setStartDate(new Date());
    }
  }

  const startDateChanged = async (newStartDate: Date | null) => {
    if (newStartDate) {
      
      const today = new Date();
      if (newStartDate >= today) {
        setStartDate(newStartDate);
        setBackdate(false);
      } else if (newStartDate < firstAYStart && !outsideAY) {
        setDateString("schoolYear");
        setStartDate(firstAYStart);
        setDialogOpen(true);
      } else if (backdate) {
        setStartDate(newStartDate);
      } else {
        setDateString("date");
        setStartDate(newStartDate);
        setDialogOpen(true);
      }
    }
  }
  return (
    <Box sx={styles.availabilityOption}>
      <ConfirmDialog
            open={dialogOpen}
            header='Confirm Start Date'
            message={`The Start Date that you selected is in the past. Would you like to start this course from today or from the ${dateString === 'schoolYear' ? 'beginning of the school year': 'selected date' }?`}
            confirmString={dateString === 'schoolYear' ? 'Use School Start': 'Use Selected Date' }
            cancelString="Use Today's date"
            onCancel={onConfirmToday}
            onConfirm={onConfirmDate}
          />
        <Box sx={styles.dateRangeRow}>
          <Box sx={styles.dateRangePicker}>
            <DatePicker
              value={startDate}
              onChange={startDateChanged}
            />
          </Box>
          <span> to </span>
          <Box sx={styles.dateRangePicker}>
            <DatePicker
              value={endDate}
              onChange={setEndDate}
              minDate={getMinEndDate()}
            />
          </Box>
        </Box>
      <FormControlLabel
          control={
            <Checkbox
              checked={backdate || false}
              name='allowBackdate'
              color='secondary'
              onChange={(e) => backdateChanged(e.target.checked)}
            />
          }
          label={<Typography variant='body2'>Allow course to be backdated from date that is earlier than today</Typography>}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={outsideAY || false}
              name='outsideAcademicYear'
              color='secondary'
              onChange={(e) => setOutsideAY(e.target.checked)}
            />
          }
          label={<Typography variant='body2'>Allow schedule dates outside of academic year</Typography>}
        />
    </Box>
    );
}
